import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import Grid from 'apollo-react/components/Grid';
import IconButton from 'apollo-react/components/IconButton';
import IconMenuButton from 'apollo-react/components/IconMenuButton';
import Loader from 'apollo-react/components/Loader';
import MenuButton from 'apollo-react/components/MenuButton';
import Tooltip from 'apollo-react/components/Tooltip';
import Typography from 'apollo-react/components/Typography';
import Info from 'apollo-react-icons/Info';
import Menu from 'apollo-react-icons/Menu';
import qs from 'query-string';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import SaveAsModal from './SaveAsModal';
import DeleteConfirmationModal from '../../../components/DeleteConfirmationModal';
import { HOME, SAVED_SEARCH_EDITOR } from '../../../constants/Urls';
import { useSavedSearchSelector } from '../../../custom-hooks';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import { getUITA } from '../../../utils/commonUtils';
import { deleteSavedSearch, saveSavedSearchDesigner } from '../../actions/editor.actions';
import { getSavedSearchProp, hasSavedSearchUnsavedChanges } from '../../selectors/editor.selectors';

const useStyles = makeStyles({
  wrapper: {
    borderBottom: '1px solid #e9e9e9',
    padding: '20px 0px',
    '& ul': {
      marginLeft: '15px',
    },
    marginLeft: 15,
  },
  info: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'start',
    '@media (max-width: 1200px)': {
      display: 'none',
    },
  },
  infoTooltip: {
    display: 'none',
    '@media (max-width: 1200px)': {
      display: 'block',
    },
  },
  infoLabel: {
    marginRight: 10,
  },
  dotLegend: {
    display: 'flex',
    height: 24,
    marginRight: 10,
  },
  uiChanges: {
    color: 'orange',
  },
  deltaChanges: {
    color: 'blue',
  },
  addedChanges: {
    color: 'green',
  },
  titleWrapper: {
    paddingLeft: 5,
    '@media (max-width: 1200px)': {
      display: 'flex',
      alignItems: 'center',
    },
  },
  buttonsWrapper: {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    '@media (max-width: 1200px)': {
      paddingRight: 28,
    },
  },
  hiddenMenuButton: {
    display: 'none',
    '@media (max-width: 1200px)': {
      display: 'block',
    },
  },
  marginRight: {
    marginRight: 15,
    '@media (max-width: 1200px)': {
      display: 'none',
    },
  },
  resetOverflow: {
    overflow: 'visible!important',
    minWidth: 'initial!important',
  },
});

const Header = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const translateString = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteModalOpened, setIsDeleteModalOpened] = useState(false);
  const [isSaveAsModalOpen, setIsSaveAsModalOpen] = useState(false);

  const hasUnsavedChanges = useSavedSearchSelector(hasSavedSearchUnsavedChanges);
  const savedSearchName = useSavedSearchSelector(getSavedSearchProp, { prop: 'searchName' });
  const translatedEntityName = useSavedSearchSelector(getSavedSearchProp, {
    prop: 'translatedEntityName',
  });

  const { isNew, entityName, translatedEntityName: translatedEntityNameFromUrl } =
    qs.parse(location.search) || {};

  const entityNameTitle = translatedEntityName || translatedEntityNameFromUrl;

  const redirectToPrevPage = () => {
    if (history.length > 1) {
      history.goBack();
    } else {
      history.push(`${HOME}`);
    }
  };

  const redirectToSavedSearch = ({ savedSearchId, entityName }) => {
    history.replace(
      `${SAVED_SEARCH_EDITOR}?id=${savedSearchId}&entityName=${entityName}&standaloneLayout=true`
    );
  };

  const handleCancel = () => {
    redirectToPrevPage();
  };

  const handleSave = () => {
    dispatch(saveSavedSearchDesigner({ setIsLoading, isNew, redirectToPrevPage }));
  };

  const handleDeleteSavedSearch = async () => {
    setIsDeleteModalOpened(false);
    setIsLoading(true);
    await dispatch(deleteSavedSearch({ redirectToPrevPage }));
    setIsLoading(false);
  };

  const actionItems = [
    {
      text: translateString('Delete'),
      onClick: () => setIsDeleteModalOpened(true),
      hidden: isNew,
    },
    {
      text: translateString('Save As'),
      onClick: () => setIsSaveAsModalOpen(true),
    },
  ];

  const sortedActionItems = actionItems.sort((a, b) => a.text.localeCompare(b.text));

  const menuItems = [
    ...sortedActionItems,
    {
      text: translateString('Cancel'),
      onClick: handleCancel,
    },
    {
      text: translateString('Save'),
      onClick: handleSave,
      disabled: !hasUnsavedChanges,
    },
  ];

  const savedSearchArr = [
    {
      title: `${translateString('Entity Name')}: ${entityNameTitle}`,
      subtitle: '',
    },
  ];

  const deleteConfirmMessage = (
    <>
      {translateString('Do you really want to delete')} {'Saved Search '}
      <b>{savedSearchName}</b>
      {'?'}
    </>
  );

  return (
    <div className={classes.wrapper}>
      <Grid container justifyContent="flex-end" alignItems="center">
        <Grid className={classes.titleWrapper} item xs={8}>
          <span className={classes.infoTooltip}>
            {entityName && (
              <>
                <Tooltip extraLabels={savedSearchArr} placement="right" variant="light">
                  <IconButton aria-label="Info Icon">
                    <Info color="primary" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </span>
          <div className={classes.info}>
            <Typography className={classes.infoLabel}>
              {`${translateString('Entity Name')}: `}
            </Typography>
            <Typography>{entityNameTitle}</Typography>
          </div>
        </Grid>
        <Grid className={classes.buttonsWrapper} item xs={4}>
          <IconMenuButton
            id="menu"
            className={classes.hiddenMenuButton}
            menuItems={menuItems.filter((action) => !action.hidden)}
          >
            <Menu />
          </IconMenuButton>
          {!isNew && (
            <MenuButton
              buttonText={translateString('Actions')}
              menuItems={sortedActionItems.filter((action) => !action.hidden)}
              className={`${classes.marginRight} ${classes.resetOverflow}`}
            />
          )}
          <Button
            variant="text"
            className={`${classes.marginRight} ${classes.resetOverflow}`}
            onClick={handleCancel}
            ui-ta={getUITA('Editor Header Header Button', 'Cancel')}
          >
            {translateString('Cancel')}
          </Button>
          <Button
            variant="secondary"
            className={`${classes.marginRight} ${classes.resetOverflow}`}
            onClick={handleSave}
            disabled={!hasUnsavedChanges}
            ui-ta={getUITA('Entity Editor Header Button', 'Save')}
          >
            {translateString('Save')}
          </Button>
          {isLoading && <Loader isInner />}
        </Grid>
        <DeleteConfirmationModal
          message={deleteConfirmMessage}
          open={isDeleteModalOpened}
          onClose={() => setIsDeleteModalOpened(false)}
          onDeleteConfirm={handleDeleteSavedSearch}
        />
        {isSaveAsModalOpen && (
          <SaveAsModal
            initialSavedSearchName={savedSearchName}
            entityName={entityName}
            isOpen={isSaveAsModalOpen}
            handleClose={() => setIsSaveAsModalOpen(false)}
            setIsLoading={setIsLoading}
            redirectToSavedSearch={redirectToSavedSearch}
          />
        )}
      </Grid>
    </div>
  );
};

export default Header;
